import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  extractPincodeLocationFromRecord,
  getPincodeByStateAndCity,
  getPincodeMasterRecord,
  normalizePincodeInput,
} from '../../api/formService';
import {
  useCityOptionsForState,
  useProposalStateOptions,
} from './useProposalStateCityOptions';

const DEFAULT_FIELDS = {
  pinCode: 'pinCode',
  state: 'state',
  city: 'city',
};

/**
 * Bidirectional pincode ↔ state/city sync for proposal address blocks.
 * - 6-digit pin → state + city via getPincodeMasterRecord
 * - state + city → pin via getPincodeByStateAndCity (on city select and when pin is empty)
 */
export function useProposalAddressLocation({
  values = {},
  onChange,
  applyFields,
  fieldNames = DEFAULT_FIELDS,
  externalPinSources = [],
  clearOnInvalidPin = true,
}) {
  const fields = { ...DEFAULT_FIELDS, ...fieldNames };
  const pinCode = values[fields.pinCode] ?? '';
  const state = values[fields.state] ?? '';
  const city = values[fields.city] ?? '';

  const [isLocationLocked, setIsLocationLocked] = useState(false);
  const lastResolvedPinRef = useRef(null);
  const { stateOptions, findStateID } = useProposalStateOptions();
  const { cityOptions } = useCityOptionsForState(state, findStateID);

  const commitFields = useCallback(
    (updates) => {
      if (applyFields) {
        applyFields(updates);
        return;
      }
      Object.entries(updates).forEach(([field, value]) => {
        onChange(field, value);
      });
    },
    [applyFields, onChange],
  );

  const resolvedPin = useMemo(
    () => normalizePincodeInput(pinCode),
    [pinCode],
  );

  const hasAppliedExternalPinRef = useRef(false);

  // One-time backfill from lead/Aadhaar when the pin field is still empty on load.
  useEffect(() => {
    if (hasAppliedExternalPinRef.current) return undefined;
    if (normalizePincodeInput(pinCode).length === 6) {
      hasAppliedExternalPinRef.current = true;
      return undefined;
    }

    for (const src of externalPinSources) {
      const normalized = normalizePincodeInput(src);
      if (normalized.length === 6) {
        hasAppliedExternalPinRef.current = true;
        commitFields({ [fields.pinCode]: normalized });
        break;
      }
    }
    return undefined;
  }, [
    pinCode,
    externalPinSources,
    fields.pinCode,
    commitFields,
  ]);

  const needsPinLookup = useMemo(() => {
    if (resolvedPin.length !== 6) return false;
    const stateName = String(state ?? '').trim();
    const cityName = String(city ?? '').trim();
    return !stateName || !cityName;
  }, [resolvedPin, state, city]);

  useEffect(() => {
    if (!resolvedPin) {
      lastResolvedPinRef.current = null;
      setIsLocationLocked(false);
      return undefined;
    }

    if (
      !needsPinLookup &&
      lastResolvedPinRef.current === resolvedPin &&
      String(state ?? '').trim() &&
      String(city ?? '').trim()
    ) {
      setIsLocationLocked(true);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const match = await getPincodeMasterRecord(resolvedPin);
        if (cancelled) return;

        if (match) {
          const location = extractPincodeLocationFromRecord(match);
          const updates = {};
          if (normalizePincodeInput(pinCode) !== resolvedPin) {
            updates[fields.pinCode] = resolvedPin;
          }
          if (location.state) {
            updates[fields.state] = location.state;
          }
          if (location.city) {
            updates[fields.city] = location.city;
          }
          if (Object.keys(updates).length > 0) {
            commitFields(updates);
          }
          lastResolvedPinRef.current = resolvedPin;
          setIsLocationLocked(Boolean(location.state && location.city));
        } else if (
          clearOnInvalidPin &&
          normalizePincodeInput(pinCode) === resolvedPin
        ) {
          commitFields({
            [fields.state]: '',
            [fields.city]: '',
          });
          lastResolvedPinRef.current = null;
          setIsLocationLocked(false);
        }
      } catch {
        if (!cancelled) {
          setIsLocationLocked(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    resolvedPin,
    pinCode,
    state,
    city,
    needsPinLookup,
    fields.pinCode,
    fields.state,
    fields.city,
    commitFields,
    clearOnInvalidPin,
  ]);

  useEffect(() => {
    const stateName = String(state ?? '').trim();
    const cityName = String(city ?? '').trim();
    if (!stateName || !cityName) return undefined;

    const currentPin = normalizePincodeInput(pinCode);
    // Only derive pin from state+city when user picked both via dropdowns (pin still empty).
    if (currentPin.length > 0) return undefined;

    let cancelled = false;
    getPincodeByStateAndCity(stateName, cityName).then((match) => {
      if (cancelled || !match) return;
      const location = extractPincodeLocationFromRecord(match);
      if (location.pinCode) {
        commitFields({ [fields.pinCode]: location.pinCode });
        lastResolvedPinRef.current = location.pinCode;
        setIsLocationLocked(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [state, city, pinCode, fields.pinCode, commitFields]);

  const handleStateChange = useCallback(
    (_name, value) => {
      lastResolvedPinRef.current = null;
      commitFields({
        [fields.state]: value,
        [fields.city]: '',
        [fields.pinCode]: '',
      });
      setIsLocationLocked(false);
    },
    [fields.state, fields.city, fields.pinCode, commitFields],
  );

  const handleCityChange = useCallback(
    async (_name, value) => {
      commitFields({ [fields.city]: value });
      const stateName = String(state ?? '').trim();
      if (!stateName || !value) return;

      try {
        const match = await getPincodeByStateAndCity(stateName, value);
        if (match) {
          const location = extractPincodeLocationFromRecord(match);
          if (location.pinCode) {
            commitFields({ [fields.pinCode]: location.pinCode });
            lastResolvedPinRef.current = location.pinCode;
            setIsLocationLocked(true);
          }
        }
      } catch {
        // best-effort lookup
      }
    },
    [fields.city, fields.pinCode, commitFields, state],
  );

  const handlePinCodeChange = useCallback(
    (_name, value) => {
      hasAppliedExternalPinRef.current = true;
      const nextPin = normalizePincodeInput(value);
      lastResolvedPinRef.current = null;
      setIsLocationLocked(false);

      if (nextPin.length < 6) {
        commitFields({
          [fields.pinCode]: nextPin,
          [fields.state]: '',
          [fields.city]: '',
        });
        return;
      }

      commitFields({ [fields.pinCode]: nextPin });
    },
    [fields.pinCode, fields.state, fields.city, commitFields],
  );

  return {
    stateOptions,
    cityOptions,
    handleStateChange,
    handleCityChange,
    handlePinCodeChange,
    isLocationLocked,
  };
}
